import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Shuffle, RefreshCw, Printer, Download, Plus, Lock, Unlock, CheckCircle, UserPlus, Trash2, Pencil } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const DrawsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [draws, setDraws] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [addingEntries, setAddingEntries] = useState({});
  
  // Modal state for adding contestant
  const [showAddContestantModal, setShowAddContestantModal] = useState(false);
  const [selectedDrawForAdd, setSelectedDrawForAdd] = useState(null);
  const [addMode, setAddMode] = useState("existing"); // "existing" or "new"
  const [addContestantData, setAddContestantData] = useState({
    contestant_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age_group: "mini",
    is_member: false,
    team_roping_partner: "",
    ribbon_roping_partner: ""
  });
  const [submittingContestant, setSubmittingContestant] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null); // { drawId, entryId }
  const [editPartnerValue, setEditPartnerValue] = useState("");

  useEffect(() => {
    fetchPlaydays();
    fetchContestants();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchDraws();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchContestants = async () => {
    try {
      const response = await axios.get(`${API}/contestants`);
      setContestants(response.data);
    } catch (error) {
      console.error("Failed to fetch contestants");
    }
  };

  const fetchDraws = async () => {
    try {
      const response = await axios.get(`${API}/draws?playday_id=${selectedPlayday}`);
      setDraws(response.data);
    } catch (error) {
      toast.error("Failed to fetch draws");
    }
  };

  const generateDraws = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/draws/generate/${selectedPlayday}`);
      toast.success("Draws generated successfully");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate draws");
    } finally {
      setGenerating(false);
    }
  };

  const shuffleDraw = async (drawId) => {
    try {
      await axios.put(`${API}/draws/${drawId}/shuffle`);
      toast.success("Draw order shuffled");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to shuffle draw");
    }
  };

  const addEntriesToDraw = async (drawId) => {
    setAddingEntries(prev => ({ ...prev, [drawId]: true }));
    try {
      const response = await axios.put(`${API}/draws/${drawId}/add-entries`);
      toast.success(response.data.message);
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add entries");
    } finally {
      setAddingEntries(prev => ({ ...prev, [drawId]: false }));
    }
  };

  const openAddContestantModal = (draw) => {
    setSelectedDrawForAdd(draw);
    setAddMode("existing");
    setAddContestantData({
      contestant_id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      age_group: "mini",
      is_member: false,
      team_roping_partner: "",
      ribbon_roping_partner: ""
    });
    setShowAddContestantModal(true);
  };

  const handleAddContestant = async (e) => {
    e.preventDefault();
    
    if (addMode === "existing" && !addContestantData.contestant_id) {
      toast.error("Please select a contestant");
      return;
    }
    
    if (addMode === "new" && (!addContestantData.first_name || !addContestantData.last_name)) {
      toast.error("Please enter first and last name");
      return;
    }
    
    setSubmittingContestant(true);
    try {
      const payload = addMode === "existing" 
        ? {
            contestant_id: addContestantData.contestant_id,
            team_roping_partner: addContestantData.team_roping_partner || null,
            ribbon_roping_partner: addContestantData.ribbon_roping_partner || null
          }
        : {
            first_name: addContestantData.first_name,
            last_name: addContestantData.last_name,
            email: addContestantData.email || null,
            phone: addContestantData.phone || null,
            age_group: addContestantData.age_group,
            is_member: addContestantData.is_member,
            team_roping_partner: addContestantData.team_roping_partner || null,
            ribbon_roping_partner: addContestantData.ribbon_roping_partner || null
          };
      
      const response = await axios.post(`${API}/draws/${selectedDrawForAdd.id}/add-contestant`, payload);
      toast.success(response.data.message);
      setShowAddContestantModal(false);
      fetchDraws();
      fetchContestants(); // Refresh contestants list if new one was added
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contestant");
    } finally {
      setSubmittingContestant(false);
    }
  };

  const unlockDraw = async (drawId) => {
    try {
      await axios.put(`${API}/draws/${drawId}/unlock`);
      toast.success("Draw unlocked for editing");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock draw");
    }
  };

  const unlockAllDraws = async () => {
    try {
      const response = await axios.put(`${API}/playdays/${selectedPlayday}/unlock-all-draws`);
      toast.success(response.data.message);
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock draws");
    }
  };

  const finalizePlayday = async () => {
    if (!window.confirm("Are you sure you want to finalize this playday? All data will be protected from further changes.")) {
      return;
    }
    try {
      await axios.put(`${API}/playdays/${selectedPlayday}/finalize`);
      toast.success("Playday finalized successfully");
      fetchPlaydays();
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to finalize playday");
    }
  };

  const unfinalizePlayday = async () => {
    try {
      await axios.put(`${API}/playdays/${selectedPlayday}/unfinalize`);
      toast.success("Playday unlocked for editing");
      fetchPlaydays();
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unfinalize playday");
    }
  };

  const deleteEntryFromDraw = async (drawId, entryId, contestantName, contestant2Name) => {
    const isTeam = !!contestant2Name;
    const confirmMsg = isTeam 
      ? `Delete team "${contestantName} & ${contestant2Name}" from this draw?`
      : `Delete "${contestantName}" from this draw?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/draws/${drawId}/entries/${entryId}`);
      toast.success(isTeam ? "Team deleted from draw" : "Contestant deleted from draw");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete from draw");
    }
  };

  const savePartnerEdit = async (drawId, entryId) => {
    try {
      const selectedContestant = contestants.find(c => `${c.first_name} ${c.last_name}` === editPartnerValue);
      await axios.put(`${API}/draws/${drawId}/entry/${entryId}/partner`, {
        contestant2_name: editPartnerValue,
        contestant2_id: selectedContestant?.id || null
      });
      toast.success("Partner updated");
      setEditingPartner(null);
      setEditPartnerValue("");
      fetchDraws();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update partner");
    }
  };

  // Check if any draws are locked
  const hasLockedDraws = () => {
    return draws.some(d => d.is_locked);
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const getSelectedPlayday = () => {
    return playdays.find(p => p.id === selectedPlayday);
  };

  const isPlaydayFinalized = () => {
    const playday = getSelectedPlayday();
    return playday?.is_finalized || false;
  };

  const isRopingEvent = (eventName) => {
    return eventName.includes("Team Roping") || eventName.includes("Ribbon Roping");
  };

  const isTeamRoping = (eventName) => {
    return eventName.includes("Team Roping");
  };

  const isRibbonRoping = (eventName) => {
    return eventName.includes("Ribbon Roping");
  };

  // Get contestants not already in the selected draw
  const getAvailableContestants = () => {
    if (!selectedDrawForAdd) return contestants;
    const existingIds = new Set(selectedDrawForAdd.entries.map(e => e.contestant_id));
    return contestants
      .filter(c => !existingIds.has(c.id))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  };

  const handleExportDraws = () => {
    const allEntries = [];
    draws.forEach(draw => {
      draw.entries.forEach(entry => {
        const rowData = {
          event: draw.event_name,
          draw_order: entry.draw_order,
          contestant: entry.contestant_name,
          time: entry.time || "",
          penalty: entry.penalty || "",
          total: entry.total || ""
        };
        if (isRopingEvent(draw.event_name)) {
          rowData.partner = entry.contestant2_name || "";
        }
        allEntries.push(rowData);
      });
    });
    
    const headers = ["event", "draw_order", "contestant"];
    if (draws.some(d => isRopingEvent(d.event_name))) {
      headers.push("partner");
    }
    headers.push("time", "penalty", "total");
    
    exportToCSV(
      allEntries, 
      `draws_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      headers
    );
    toast.success("Draws exported to CSV");
  };

  const handlePrintDraws = () => {
    handlePrint("Draw Order", getSelectedPlaydayName());
  };

  const handlePrintAllDraws = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups to print.");
      return;
    }

    const playdayName = getSelectedPlaydayName();
    let tablesHtml = "";

    draws.forEach((draw) => {
      const isRoping = isRopingEvent(draw.event_name);
      const partnerHeader = isRoping ? "<th style='text-align:left;padding:8px 12px;border-bottom:2px solid #ccc;'>Contestant 2</th>" : "";

      let rowsHtml = "";
      draw.entries.forEach((entry) => {
        const partnerCell = isRoping
          ? `<td style='padding:6px 12px;border-bottom:1px solid #eee;'>${entry.contestant2_name || "-"}</td>`
          : "";
        rowsHtml += `<tr>
          <td style='padding:6px 12px;border-bottom:1px solid #eee;font-weight:bold;'>${entry.draw_order}</td>
          <td style='padding:6px 12px;border-bottom:1px solid #eee;'>${entry.contestant_name}</td>
          ${partnerCell}
          <td style='padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-family:monospace;'>${entry.time ? entry.time.toFixed(3) + "s" : ""}</td>
          <td style='padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-family:monospace;'>${entry.penalty ? "+" + entry.penalty.toFixed(1) + "s" : ""}</td>
          <td style='padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-weight:bold;font-family:monospace;'>${entry.total ? entry.total.toFixed(3) + "s" : ""}</td>
        </tr>`;
      });

      tablesHtml += `
        <div style="page-break-inside:avoid;margin-bottom:24px;">
          <h2 style="margin:0 0 8px;font-size:16px;">${draw.event_name} <span style="font-weight:normal;color:#666;">(${draw.entries.length} entries)</span></h2>
          <table style="width:100%;border-collapse:collapse;font-size:13px;">
            <thead>
              <tr style="background:#f5f5f5;">
                <th style="text-align:left;padding:8px 12px;border-bottom:2px solid #ccc;width:60px;">Draw #</th>
                <th style="text-align:left;padding:8px 12px;border-bottom:2px solid #ccc;">Contestant 1</th>
                ${partnerHeader}
                <th style="text-align:right;padding:8px 12px;border-bottom:2px solid #ccc;">Time</th>
                <th style="text-align:right;padding:8px 12px;border-bottom:2px solid #ccc;">Penalty</th>
                <th style="text-align:right;padding:8px 12px;border-bottom:2px solid #ccc;">Total</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>`;
    });

    printWindow.document.write(`<!DOCTYPE html><html><head><title>All Draws - ${playdayName}</title>
      <style>body{font-family:Arial,sans-serif;margin:24px;} @media print{body{margin:12px;}}</style>
      </head><body>
        <h1 style="margin:0 0 4px;font-size:20px;">TTP Play Day - Draw Order</h1>
        <p style="margin:0 0 20px;color:#666;">${playdayName}</p>
        ${tablesHtml}
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="draws-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Draws</h1>
          <p className="text-text-secondary mt-1">Random draw orders by event</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintAllDraws} disabled={draws.length === 0} data-testid="print-all-draws-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print All Draws
          </Button>
          <Button variant="outline" onClick={handleExportDraws} disabled={draws.length === 0} data-testid="export-draws-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Draw Order</h1>
        <p>{getSelectedPlaydayName()}</p>
      </div>

      {/* Controls */}
      <div className="flex gap-4 items-end flex-wrap no-print">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date}) {p.is_finalized && "✓"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={generateDraws} 
          disabled={generating || isPlaydayFinalized()}
          className="bg-primary text-primary-foreground hover:bg-primary-hover"
          data-testid="generate-draws-btn"
        >
          {generating ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Shuffle className="h-4 w-4 mr-2" />
          )}
          Generate Draws
        </Button>
        
        {/* Unlock All Draws Button - shown when there are locked draws */}
        {hasLockedDraws() && !isPlaydayFinalized() && (
          <Button 
            onClick={unlockAllDraws}
            variant="outline"
            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
            data-testid="unlock-all-draws-btn"
          >
            <Unlock className="h-4 w-4 mr-2" />
            Unlock All Draws
          </Button>
        )}
        
        {/* Finalize/Unfinalize Button */}
        {isPlaydayFinalized() ? (
          <Button 
            onClick={unfinalizePlayday}
            variant="outline"
            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
            data-testid="unfinalize-playday-btn"
          >
            <Unlock className="h-4 w-4 mr-2" />
            Unlock Playday
          </Button>
        ) : (
          <Button 
            onClick={finalizePlayday}
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
            disabled={draws.length === 0}
            data-testid="finalize-playday-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Finalize Playday
          </Button>
        )}
      </div>

      {/* Finalized Status Banner */}
      {isPlaydayFinalized() && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">This playday is finalized. All data is protected.</span>
        </div>
      )}

      {/* Draws by Event */}
      {draws.length === 0 ? (
        <div className="bg-white rounded-xl border border-primary/20 p-12 text-center">
          <Shuffle className="h-12 w-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary">No draws generated yet</p>
          <p className="text-text-muted text-sm mt-1">Click "Generate Draws" to create random draw orders</p>
        </div>
      ) : (
        <Tabs defaultValue={draws[0]?.id} className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 no-print">
            {draws.map((draw) => (
              <TabsTrigger 
                key={draw.id} 
                value={draw.id}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground bg-white border border-primary/20"
              >
                {draw.event_name}
                {draw.is_locked && <Lock className="h-3 w-3 ml-1" />}
              </TabsTrigger>
            ))}
          </TabsList>

          {draws.map((draw) => (
            <TabsContent key={draw.id} value={draw.id} className="print:block">
              <div className="bg-white rounded-xl border border-primary/20 overflow-hidden mb-6">
                <div className="p-4 border-b border-primary/20 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-text-primary flex items-center gap-2">
                      {draw.event_name}
                      {draw.is_locked && <Lock className="h-4 w-4 text-yellow-600" />}
                    </h3>
                    <p className="text-sm text-text-secondary">{draw.entries.length} entries</p>
                  </div>
                  <div className="flex gap-2 no-print">
                    {/* Unlock button - shown when locked */}
                    {draw.is_locked && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unlockDraw(draw.id)}
                        disabled={isPlaydayFinalized()}
                        className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                        data-testid={`unlock-${draw.id}`}
                      >
                        <Unlock className="h-4 w-4 mr-2" />
                        Unlock to Edit
                      </Button>
                    )}
                    
                    {/* Action buttons - always shown, disabled when locked */}
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => openAddContestantModal(draw)}
                      disabled={isPlaydayFinalized() || draw.is_locked}
                      className="bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
                      data-testid={`add-contestant-${draw.id}`}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add to Draw
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addEntriesToDraw(draw.id)}
                      disabled={addingEntries[draw.id] || isPlaydayFinalized() || draw.is_locked}
                      data-testid={`add-entries-${draw.id}`}
                    >
                      {addingEntries[draw.id] ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add from Registrations
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => shuffleDraw(draw.id)}
                      disabled={isPlaydayFinalized() || draw.is_locked}
                      data-testid={`shuffle-${draw.id}`}
                    >
                      <Shuffle className="h-4 w-4 mr-2" />
                      Re-shuffle
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading font-bold text-text-primary w-20">Draw #</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary">Contestant 1</TableHead>
                      {isRopingEvent(draw.event_name) && (
                        <TableHead className="font-heading font-bold text-text-primary">Contestant 2</TableHead>
                      )}
                      <TableHead className="font-heading font-bold text-text-primary text-right">Time</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Penalty</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Total</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {draw.entries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isRopingEvent(draw.event_name) ? 7 : 6} className="text-center py-8 text-text-secondary">
                          No entries for this event
                        </TableCell>
                      </TableRow>
                    ) : (
                      draw.entries.map((entry) => (
                        <TableRow key={entry.id} className="hover:bg-surface-muted" data-testid={`draw-entry-${entry.id}`}>
                          <TableCell className="font-heading font-bold text-lg text-primary">
                            {entry.draw_order}
                          </TableCell>
                          <TableCell className="font-medium text-text-primary">
                            {entry.contestant_name}
                          </TableCell>
                          {isRopingEvent(draw.event_name) && (
                            <TableCell className="text-text-secondary">
                              {editingPartner?.drawId === draw.id && editingPartner?.entryId === entry.id ? (
                                <div className="flex items-center gap-1">
                                  <Select value={editPartnerValue} onValueChange={setEditPartnerValue}>
                                    <SelectTrigger className="h-8 text-xs w-40">
                                      <SelectValue placeholder="Select partner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {contestants
                                        .filter(c => c.id !== entry.contestant_id)
                                        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                                        .map(c => (
                                          <SelectItem key={c.id} value={`${c.first_name} ${c.last_name}`}>
                                            {c.first_name} {c.last_name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                  <Button size="sm" className="h-8 px-2" onClick={() => savePartnerEdit(draw.id, entry.id)} data-testid={`save-partner-${entry.id}`}>
                                    <CheckCircle className="h-3 w-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingPartner(null)}>
                                    X
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span>{entry.contestant2_name || '-'}</span>
                                  {!draw.is_locked && !isPlaydayFinalized() && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-text-muted hover:text-primary"
                                      onClick={() => {
                                        setEditingPartner({ drawId: draw.id, entryId: entry.id });
                                        setEditPartnerValue(entry.contestant2_name || "");
                                      }}
                                      data-testid={`edit-partner-${entry.id}`}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-right font-mono text-text-secondary">
                            {entry.time ? `${entry.time.toFixed(3)}s` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-text-secondary">
                            {entry.penalty ? `+${entry.penalty.toFixed(1)}s` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-text-primary">
                            {entry.total ? `${entry.total.toFixed(3)}s` : '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteEntryFromDraw(draw.id, entry.id, entry.contestant_name, entry.contestant2_name)}
                              disabled={isPlaydayFinalized() || draw.is_locked || entry.time !== null}
                              className="h-8 w-8 p-0 text-status-error hover:text-status-error hover:bg-status-error/10"
                              title={entry.time !== null ? "Cannot delete entry with recorded time" : (isRopingEvent(draw.event_name) ? "Delete team from draw" : "Delete from draw")}
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Add Contestant Modal */}
      <Dialog open={showAddContestantModal} onOpenChange={setShowAddContestantModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add to Draw</DialogTitle>
            {selectedDrawForAdd && (
              <p className="text-sm text-text-secondary">
                Adding to: <span className="font-medium text-primary">{selectedDrawForAdd.event_name}</span>
              </p>
            )}
          </DialogHeader>
          
          {/* Toggle between existing and new contestant */}
          <div className="flex gap-2 border-b pb-4">
            <Button
              type="button"
              variant={addMode === "existing" ? "default" : "outline"}
              size="sm"
              onClick={() => setAddMode("existing")}
              className={addMode === "existing" ? "bg-primary text-primary-foreground" : ""}
            >
              Select Existing
            </Button>
            <Button
              type="button"
              variant={addMode === "new" ? "default" : "outline"}
              size="sm"
              onClick={() => setAddMode("new")}
              className={addMode === "new" ? "bg-primary text-primary-foreground" : ""}
            >
              Add New Contestant
            </Button>
          </div>

          <form onSubmit={handleAddContestant} className="space-y-4">
            {addMode === "existing" ? (
              <div className="space-y-2">
                <Label>Select Contestant *</Label>
                <Select
                  value={addContestantData.contestant_id}
                  onValueChange={(v) => setAddContestantData(prev => ({ ...prev, contestant_id: v }))}
                >
                  <SelectTrigger data-testid="add-contestant-select">
                    <SelectValue placeholder="Select a contestant" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableContestants().map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} ({c.age_group})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={addContestantData.first_name}
                      onChange={(e) => setAddContestantData(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="First name"
                      data-testid="new-contestant-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={addContestantData.last_name}
                      onChange={(e) => setAddContestantData(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Last name"
                      data-testid="new-contestant-lastname"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={addContestantData.email}
                      onChange={(e) => setAddContestantData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={addContestantData.phone}
                      onChange={(e) => setAddContestantData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="555-1234"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Age Group</Label>
                    <Select
                      value={addContestantData.age_group}
                      onValueChange={(v) => setAddContestantData(prev => ({ ...prev, age_group: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mini">Mini</SelectItem>
                        <SelectItem value="junior">Junior</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                        <SelectItem value="adult">Adult</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="is_member"
                      checked={addContestantData.is_member}
                      onCheckedChange={(checked) => setAddContestantData(prev => ({ ...prev, is_member: checked }))}
                    />
                    <Label htmlFor="is_member" className="cursor-pointer">Member</Label>
                  </div>
                </div>
              </div>
            )}

            {/* Partner fields for roping events */}
            {selectedDrawForAdd && isTeamRoping(selectedDrawForAdd.event_name) && (
              <div className="space-y-2">
                <Label>Team Roping Partner</Label>
                <Select
                  value={addContestantData.team_roping_partner}
                  onValueChange={(value) => setAddContestantData(prev => ({ ...prev, team_roping_partner: value }))}
                >
                  <SelectTrigger data-testid="team-roping-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {contestants
                      .filter(c => c.id !== addContestantData.contestant_id)
                      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                      .map(c => (
                        <SelectItem key={c.id} value={`${c.first_name} ${c.last_name}`}>
                          {c.first_name} {c.last_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedDrawForAdd && isRibbonRoping(selectedDrawForAdd.event_name) && (
              <div className="space-y-2">
                <Label>Ribbon Roping Partner</Label>
                <Select
                  value={addContestantData.ribbon_roping_partner}
                  onValueChange={(value) => setAddContestantData(prev => ({ ...prev, ribbon_roping_partner: value }))}
                >
                  <SelectTrigger data-testid="ribbon-roping-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {contestants
                      .filter(c => c.id !== addContestantData.contestant_id)
                      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                      .map(c => (
                        <SelectItem key={c.id} value={`${c.first_name} ${c.last_name}`}>
                          {c.first_name} {c.last_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddContestantModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submittingContestant || (addMode === "existing" && !addContestantData.contestant_id) || (addMode === "new" && (!addContestantData.first_name || !addContestantData.last_name))}
                className="bg-primary text-primary-foreground hover:bg-primary-hover"
                data-testid="submit-add-contestant"
              >
                {submittingContestant ? "Adding..." : "Add to Draw"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DrawsPage;
